/** Open a print dialog for a single question document (not the full app page). */

import {
  htmlWithArtifacts,
  type ArtifactLike,
} from "../../../../utils/generatedArtifacts"

export interface PrintQuestionOptions {
  title: string
  html: string
  metaLines?: string[]
  artifacts?: ArtifactLike[] | null
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")

const buildPrintDocument = (
  title: string,
  bodyHtml: string,
  metaLines: string[]
): string => {
  const metaBlock = metaLines
    .filter(Boolean)
    .map((line) => `<p class="meta">${escapeHtml(line)}</p>`)
    .join("")

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { margin: 16mm; }
    body {
      margin: 0;
      padding: 24px;
      font-family: Georgia, "Times New Roman", serif;
      color: #0f172a;
      line-height: 1.55;
    }
    h1 {
      margin: 0 0 8px;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 18px;
      font-weight: 700;
    }
    .meta {
      margin: 0 0 4px;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 12px;
      color: #64748b;
    }
    .divider {
      border: 0;
      border-top: 1px solid #e2e8f0;
      margin: 16px 0;
    }
    .content img {
      max-width: 100%;
      height: auto !important;
    }
    .content p { margin: 0 0 12px; }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  ${metaBlock}
  <hr class="divider" />
  <div class="content">${bodyHtml}</div>
</body>
</html>`
}

/**
 * Print via a hidden iframe so we don't spawn a second browser tab,
 * and call print() only once (no onload + print double-fire).
 */
export const printQuestionHtml = ({
  title,
  html,
  metaLines = [],
  artifacts,
}: PrintQuestionOptions): void => {
  const bodyHtml = htmlWithArtifacts(html, artifacts)
  if (!bodyHtml) return

  const documentHtml = buildPrintDocument(title, bodyHtml, metaLines)

  const existing = document.getElementById("learner-print-frame")
  if (existing) existing.remove()

  const iframe = document.createElement("iframe")
  iframe.id = "learner-print-frame"
  iframe.setAttribute("aria-hidden", "true")
  iframe.setAttribute("title", "Print preview")
  iframe.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;pointer-events:none"

  document.body.appendChild(iframe)

  const win = iframe.contentWindow
  const doc = iframe.contentDocument || win?.document
  if (!win || !doc) {
    iframe.remove()
    return
  }

  let printed = false
  const cleanup = () => {
    window.setTimeout(() => {
      if (iframe.parentNode) iframe.remove()
    }, 300)
  }

  const triggerPrint = () => {
    if (printed) return
    printed = true
    try {
      win.focus()
      win.print()
    } finally {
      // afterprint is not reliable in every browser; always schedule cleanup
      cleanup()
    }
  }

  win.addEventListener("afterprint", cleanup, { once: true })

  doc.open()
  doc.write(documentHtml)
  doc.close()

  // Wait for layout/images, then print once.
  const schedule = () => window.setTimeout(triggerPrint, 100)
  if (doc.readyState === "complete") {
    schedule()
  } else {
    win.addEventListener("load", schedule, { once: true })
    // Safety if load never fires
    window.setTimeout(() => {
      if (!printed) triggerPrint()
    }, 800)
  }
}

/** Download resource as PDF URL when present, otherwise as an HTML file. */
export const downloadQuestionResource = ({
  filename,
  title,
  content,
  pdfUrl,
  artifacts,
}: {
  filename: string
  title: string
  content: string | null | undefined
  pdfUrl?: string | null
  artifacts?: ArtifactLike[] | null
}): boolean => {
  if (pdfUrl) {
    const anchor = document.createElement("a")
    anchor.href = pdfUrl
    anchor.target = "_blank"
    anchor.rel = "noopener noreferrer"
    anchor.download = filename.endsWith(".pdf") ? filename : `${filename}.pdf`
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    return true
  }

  const html = htmlWithArtifacts(content, artifacts)
  if (!html) return false

  const wrapped = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { font-family: Georgia, "Times New Roman", serif; padding: 24px; line-height: 1.55; color: #0f172a; }
    img { max-width: 100%; height: auto !important; }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  ${html}
</body>
</html>`

  const blob = new Blob([wrapped], { type: "text/html;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  const safeName = filename.replace(/[^\w.\-]+/g, "_")
  anchor.href = url
  anchor.download = safeName.endsWith(".html") ? safeName : `${safeName}.html`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  return true
}
